import { Bike, Calendar, ClipboardList, RefreshCw, Users } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL, { API_ENDPOINTS, buildApiUrl } from '../config/api.js';
import { useAuth } from '../contexts/AuthContext';

const fmtDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium', timeStyle: 'short' }).format(d);
};

const statusLabel = (status) => {
  switch (String(status || '').toUpperCase()) {
    case 'PENDING':
      return 'Pendiente';
    case 'CONFIRMED':
      return 'Confirmada';
    case 'CANCELLED':
      return 'Cancelada';
    case 'COMPLETED':
      return 'Completada';
    default:
      return status || '—';
  }
};

const getCustomerFromBooking = (b) => {
  const fullName = (b?.customer?.fullName || b?.guestName || '').trim();
  const email = (b?.customer?.email || b?.guestEmail || '').trim();
  const phone = (b?.customer?.phone || b?.guestPhone || '').trim();
  const id = b?.customer?.id ?? null;

  return {
    id,
    fullName: fullName || 'Cliente sin nombre',
    email: email || '—',
    phone: phone || '—',
    bookingId: b?.id ?? null,
    createdAt: b?.createdAt || b?.created_at || b?.created || null,
  };
};

const Home = () => {
  const { user, authenticatedFetch } = useAuth();
  const navigate = useNavigate();

  // Redirigir usuarios AGENCY directamente a reservas
  useEffect(() => {
    if (user?.role === 'AGENCY') {
      navigate('/reservas', { replace: true });
    }
  }, [user, navigate]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bookingStats, setBookingStats] = useState(null); // { PENDING, CONFIRMED, COMPLETED, ... }
  const [recentBookings, setRecentBookings] = useState([]); // bookings[]
  const [vehiclesSummary, setVehiclesSummary] = useState(null); // { total, available, inUse, maintenance }

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const bookingsStatsUrl = (() => {
        let url = `${buildApiUrl(API_ENDPOINTS.BOOKINGS)}/stats`;
        if (user?.role === 'AGENCY' && user?.agencyCode) {
          url += `?agencyCode=${encodeURIComponent(user.agencyCode)}`;
        }
        return url;
      })();

      const bookingsRecentUrl = (() => {
        const params = new URLSearchParams({ page: '1', limit: '8' });
        if (user?.role === 'AGENCY' && user?.agencyCode) {
          params.set('agencyCode', user.agencyCode);
          params.set('paymentMethod', 'COLABORADOR');
        }
        return `${buildApiUrl(API_ENDPOINTS.BOOKINGS)}?${params.toString()}`;
      })();

      // En el admin ya se consume /api/vehicles (no-admin) desde Bicicletas.jsx.
      const vehiclesUrl = `${API_BASE_URL}/api/vehicles`;

      const [statsRes, bookingsRes, vehiclesRes] = await Promise.all([
        authenticatedFetch(bookingsStatsUrl),
        authenticatedFetch(bookingsRecentUrl),
        authenticatedFetch(vehiclesUrl),
      ]);

      const [statsJson, bookingsJson, vehiclesJson] = await Promise.all([
        statsRes.json().catch(() => null),
        bookingsRes.json().catch(() => null),
        vehiclesRes.json().catch(() => null),
      ]);

      if (!statsRes.ok) {
        throw new Error(statsJson?.message || 'No se pudieron cargar las estadísticas de reservas.');
      }
      if (!bookingsRes.ok) {
        throw new Error(bookingsJson?.message || 'No se pudieron cargar las reservas recientes.');
      }
      if (!vehiclesRes.ok) {
        throw new Error('No se pudieron cargar los vehículos.');
      }

      const nextBookingStats = statsJson?.success ? statsJson.data : statsJson;
      const nextBookings = bookingsJson?.success ? (bookingsJson?.data?.bookings || []) : bookingsJson?.bookings || [];
      const vehicles = Array.isArray(vehiclesJson) ? vehiclesJson : vehiclesJson?.data || [];

      const total = vehicles.length;
      const available = vehicles.filter((v) => v?.currentStatus === 'AVAILABLE').length;
      const inUse = vehicles.filter((v) => v?.currentStatus === 'IN_USE').length;
      const maintenance = vehicles.filter((v) => v?.currentStatus === 'MAINTENANCE').length;

      setBookingStats(nextBookingStats || {});
      setRecentBookings(nextBookings || []);
      setVehiclesSummary({ total, available, inUse, maintenance });
    } catch (e) {
      setError(e?.message || 'Error al cargar el dashboard.');
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, user?.agencyCode, user?.role]);

  useEffect(() => {
    // Evitar cargar dashboard si vamos a redirigir a AGENCY.
    if (user?.role === 'AGENCY') return;
    loadDashboard();
  }, [loadDashboard, user?.role]);

  const customersRecent = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const b of recentBookings || []) {
      const c = getCustomerFromBooking(b);
      const key = (c.email && c.email !== '—') ? `e:${c.email.toLowerCase()}` : (c.phone && c.phone !== '—' ? `p:${c.phone}` : `b:${c.bookingId}`);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(c);
      if (out.length >= 8) break;
    }
    return out;
  }, [recentBookings]);

  const stats = useMemo(() => {
    const pending = bookingStats?.PENDING ?? 0;
    const confirmed = bookingStats?.CONFIRMED ?? 0;
    const completed = bookingStats?.COMPLETED ?? 0;
    const totalVehicles = vehiclesSummary?.total ?? 0;
    const availableVehicles = vehiclesSummary?.available ?? 0;

    return [
      { title: 'Reservas pendientes', value: String(pending), icon: ClipboardList, color: 'orange' },
      { title: 'Reservas confirmadas', value: String(confirmed), icon: Calendar, color: 'green' },
      { title: 'Reservas completadas', value: String(completed), icon: Calendar, color: 'purple' },
      { title: `Vehículos disponibles (de ${totalVehicles})`, value: String(availableVehicles), icon: Bike, color: 'blue' },
    ];
  }, [bookingStats, vehiclesSummary]);

  return (
    <div className="content-page">
      <div className="page-header">
        <div>
          <h1>Dashboard</h1>
          <p>Bienvenido{user?.name ? `, ${user.name}` : ''}</p>
        </div>
        <button className="btn-secondary" onClick={loadDashboard} disabled={loading}>
          <RefreshCw size={16} />
          Actualizar
        </button>
      </div>

      <div className="stats-grid">
        {stats.map((stat, index) => {
          const IconComponent = stat.icon;
          return (
            <div key={index} className={`stat-card ${stat.color}`}>
              <div className="stat-icon">
                <IconComponent size={24} />
              </div>
              <div className="stat-content">
                <h3>{stat.value}</h3>
                <p>{stat.title}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="dashboard-content">
        {error && (
          <div className="recent-activity" style={{ marginBottom: 16 }}>
            <h2>Error</h2>
            <div className="activity-list">
              <div className="activity-item">
                <div className="activity-icon">
                  <Users size={16} />
                </div>
                <div className="activity-details">
                  <p>{error}</p>
                  <span className="activity-time">Revisa que el backend esté levantado y que la sesión sea válida.</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="recent-activity" style={{ marginBottom: 16 }}>
          <h2>Últimas reservas</h2>
          <div className="activity-list">
            {loading && recentBookings.length === 0 ? (
              <div className="activity-item">
                <div className="activity-icon">
                  <Calendar size={16} />
                </div>
                <div className="activity-details">
                  <p>Cargando reservas…</p>
                  <span className="activity-time">Un momento</span>
                </div>
              </div>
            ) : recentBookings.length === 0 ? (
              <div className="activity-item">
                <div className="activity-icon">
                  <Calendar size={16} />
                </div>
                <div className="activity-details">
                  <p>No hay reservas para mostrar</p>
                  <span className="activity-time">—</span>
                </div>
              </div>
            ) : (
              recentBookings.slice(0, 8).map((b) => {
                const customerName = (b?.customer?.fullName || b?.guestName || '').trim() || 'Cliente';
                const createdAt = b?.createdAt || b?.created_at || b?.created || null;
                const status = statusLabel(b?.status);
                return (
                  <div
                    key={b?.id ?? `${customerName}-${createdAt}`}
                    className="activity-item"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate('/reservas')}
                    title="Ir a Reservas"
                  >
                    <div className="activity-icon">
                      <Calendar size={16} />
                    </div>
                    <div className="activity-details">
                      <p>
                        Reserva #{b?.id ?? '—'} · {customerName} · {status}
                      </p>
                      <span className="activity-time">Creada: {fmtDateTime(createdAt)}</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="recent-activity">
          <h2>Clientes recientes (derivados de reservas)</h2>
          <div className="activity-list">
            {loading && customersRecent.length === 0 ? (
              <div className="activity-item">
                <div className="activity-icon">
                  <Users size={16} />
                </div>
                <div className="activity-details">
                  <p>Cargando clientes…</p>
                  <span className="activity-time">Un momento</span>
                </div>
              </div>
            ) : customersRecent.length === 0 ? (
              <div className="activity-item">
                <div className="activity-icon">
                  <Users size={16} />
                </div>
                <div className="activity-details">
                  <p>No hay clientes para mostrar</p>
                  <span className="activity-time">—</span>
                </div>
              </div>
            ) : (
              customersRecent.slice(0, 8).map((c) => (
                <div key={`${c.email}-${c.phone}-${c.bookingId}`} className="activity-item">
                  <div className="activity-icon">
                    <Users size={16} />
                  </div>
                  <div className="activity-details">
                    <p>{c.fullName}</p>
                    <span className="activity-time">
                      {c.email} · {c.phone}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home; 